import { isVectorSource } from '../../data/model';
import { isCapabilityAvailable, createMockEntitlement } from '../../licensing/entitlements';
import { viewForExtent } from '../../map/viewForExtent';
import { popupConfigurationHasContent } from '../../popups/validation';
import { evaluateAttributeRule } from '../../rules/evaluate';
import { matchingSelectableFeatures } from '../../selection/attributeQuery';
import {
  applySelectionMutation,
  removeSelectedFeaturesForLayer,
  toggleSelectedFeature,
} from '../../selection/selection';
import { actionError, type AppStoreActionContext } from './actionSupport';
import type { AppState } from './appState';
import { canonicalSelectionRef, layerStateContext } from './layerState';

export type ShellSelectionActions = Pick<
  AppState,
  | 'inspectFeature'
  | 'setSelectionToolActive'
  | 'setSelectionMethod'
  | 'selectFeatureFromMap'
  | 'selectFeaturesFromMap'
  | 'selectFeaturesByAttribute'
  | 'toggleFeatureSelection'
  | 'clearSelection'
  | 'clearLayerSelection'
  | 'setActiveInspectorTab'
  | 'toggleLayerVisibility'
  | 'toggleLayerExpanded'
  | 'toggleLayersPanel'
  | 'zoomToLayer'
  | 'setMockEdition'
>;

export function createShellSelectionActions({
  get,
  set,
}: AppStoreActionContext): ShellSelectionActions {
  return {
    inspectFeature: (feature) =>
      set((state) => {
        if (feature === null) {
          return state.shell.inspectedFeature
            ? { shell: { ...state.shell, inspectedFeature: null } }
            : state;
        }
        if (state.mode === 'composition') return state;
        const layer = state.project.layers.find((candidate) => candidate.id === feature.layerId);
        if (
          !layer ||
          layer.sourceId !== feature.sourceId ||
          !layer.visible ||
          !layer.popup.enabled ||
          !popupConfigurationHasContent(layer.popup)
        ) {
          return { shell: { ...state.shell, inspectedFeature: null } };
        }
        return { shell: { ...state.shell, inspectedFeature: feature } };
      }),
    setSelectionToolActive: (active) =>
      set((state) => {
        if (state.mode !== 'editor' && state.mode !== 'viewer') return state;
        if (state.shell.selectionToolActive === active) return state;
        return {
          shell: {
            ...state.shell,
            selectionToolActive: active,
            selectionMethod: active ? 'click' : null,
            annotationCreationTool: active ? null : state.shell.annotationCreationTool,
            replacingFocusAreaId: active ? null : state.shell.replacingFocusAreaId,
            annotationDraftCoordinates: active ? [] : state.shell.annotationDraftCoordinates,
            inspectedFeature: active ? null : state.shell.inspectedFeature,
          },
        };
      }),
    setSelectionMethod: (selectionMethod) =>
      set((state) => {
        if (state.mode !== 'editor' && state.mode !== 'viewer') return state;
        const selectionToolActive = selectionMethod !== null;
        if (
          state.shell.selectionMethod === selectionMethod &&
          state.shell.selectionToolActive === selectionToolActive
        ) {
          return state;
        }
        return {
          shell: {
            ...state.shell,
            selectionMethod,
            selectionToolActive,
            annotationCreationTool: selectionToolActive ? null : state.shell.annotationCreationTool,
            replacingFocusAreaId: selectionToolActive ? null : state.shell.replacingFocusAreaId,
            annotationDraftCoordinates: selectionToolActive
              ? []
              : state.shell.annotationDraftCoordinates,
            inspectedFeature: selectionToolActive ? null : state.shell.inspectedFeature,
          },
        };
      }),
    selectFeatureFromMap: (feature, mutation) =>
      set((state) => {
        if (
          (state.mode !== 'editor' && state.mode !== 'viewer') ||
          state.shell.selectionMethod !== 'click'
        ) {
          return state;
        }
        if (feature === null) {
          return state.shell.selectedFeatures.length === 0
            ? state
            : { shell: { ...state.shell, selectedFeatures: [] } };
        }
        const canonical = canonicalSelectionRef(state, feature);
        if (!canonical) return state;
        const context = layerStateContext(state, canonical.layerId);
        const sourceFeature = context?.source.data.features[canonical.sourceFeatureIndex];
        if (
          !context ||
          !sourceFeature ||
          !context.layer.visible ||
          !evaluateAttributeRule(context.layer.displayFilter, sourceFeature.properties)
        ) {
          return state;
        }
        const selectedFeatures = applySelectionMutation(
          state.shell.selectedFeatures,
          [canonical],
          mutation,
        );
        return selectedFeatures === state.shell.selectedFeatures
          ? state
          : { shell: { ...state.shell, selectedFeatures } };
      }),
    selectFeaturesFromMap: (features, mutation) =>
      set((state) => {
        if (
          (state.mode !== 'editor' && state.mode !== 'viewer') ||
          (state.shell.selectionMethod !== 'rectangle' && state.shell.selectionMethod !== 'lasso')
        ) {
          return state;
        }
        const eligible = features.flatMap((feature) => {
          const canonical = canonicalSelectionRef(state, feature);
          if (!canonical) return [];
          const context = layerStateContext(state, canonical.layerId);
          const effective = context?.source.data.features[canonical.sourceFeatureIndex];
          return context &&
            effective &&
            context.layer.visible &&
            evaluateAttributeRule(context.layer.displayFilter, effective.properties)
            ? [canonical]
            : [];
        });
        const selectedFeatures = applySelectionMutation(
          state.shell.selectedFeatures,
          eligible,
          mutation,
        );
        return selectedFeatures === state.shell.selectedFeatures
          ? state
          : { shell: { ...state.shell, selectedFeatures } };
      }),
    selectFeaturesByAttribute: (layerId, query, mutation) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'selection.byAttribute')) {
        return actionError('Select by attribute requires Placeio Maps Pro.');
      }
      if (state.mode !== 'editor' && state.mode !== 'viewer') {
        return actionError('Select by attribute is unavailable in this mode.');
      }
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const matches = matchingSelectableFeatures(
        context.layer,
        context.canonicalSource,
        context.source,
        query,
      );
      if (!matches) return actionError('The attribute query is invalid.');
      set({
        shell: {
          ...state.shell,
          selectedFeatures: applySelectionMutation(state.shell.selectedFeatures, matches, mutation),
        },
      });
      return { ok: true, matchCount: matches.length };
    },
    toggleFeatureSelection: (feature) =>
      set((state) => {
        if (state.mode !== 'editor' && state.mode !== 'viewer') return state;
        const canonical = canonicalSelectionRef(state, feature);
        if (!canonical) return state;
        const context = layerStateContext(state, canonical.layerId);
        const sourceFeature = context?.source.data.features[canonical.sourceFeatureIndex];
        if (
          !context ||
          !sourceFeature ||
          !evaluateAttributeRule(context.layer.displayFilter, sourceFeature.properties)
        ) {
          return state;
        }
        return {
          shell: {
            ...state.shell,
            selectedFeatures: toggleSelectedFeature(state.shell.selectedFeatures, canonical),
          },
        };
      }),
    clearSelection: () =>
      set((state) =>
        state.shell.selectedFeatures.length > 0
          ? { shell: { ...state.shell, selectedFeatures: [] } }
          : state,
      ),
    clearLayerSelection: (layerId) =>
      set((state) => {
        const selectedFeatures = removeSelectedFeaturesForLayer(
          state.shell.selectedFeatures,
          layerId,
        );
        return selectedFeatures === state.shell.selectedFeatures
          ? state
          : { shell: { ...state.shell, selectedFeatures } };
      }),
    setActiveInspectorTab: (activeInspectorTab) =>
      set((state) => ({ shell: { ...state.shell, activeInspectorTab } })),
    toggleLayerVisibility: (layerId) =>
      set((state) => {
        const current = state.project.layers.find((layer) => layer.id === layerId);
        if (!current) return state;
        const visible = !current.visible;
        return {
          project: {
            ...state.project,
            layers: state.project.layers.map((layer) =>
              layer.id === layerId ? { ...layer, visible } : layer,
            ),
          },
          shell: {
            ...state.shell,
            inspectedFeature:
              !visible && state.shell.inspectedFeature?.layerId === layerId
                ? null
                : state.shell.inspectedFeature,
          },
        };
      }),
    toggleLayerExpanded: (layerId) =>
      set((state) => {
        const layer = state.project.layers.find((candidate) => candidate.id === layerId);
        if (!layer || layer.style.classification.mode === 'single') return state;
        const expanded = state.shell.expandedLayerIds.includes(layerId);
        return {
          shell: {
            ...state.shell,
            expandedLayerIds: expanded
              ? state.shell.expandedLayerIds.filter((id) => id !== layerId)
              : [...state.shell.expandedLayerIds, layerId],
          },
        };
      }),
    toggleLayersPanel: () =>
      set((state) => ({
        shell: { ...state.shell, layersPanelCollapsed: !state.shell.layersPanelCollapsed },
      })),
    zoomToLayer: (layerId, viewport) =>
      set((state) => {
        const layer = state.project.layers.find((candidate) => candidate.id === layerId);
        const source = layer
          ? state.project.sources.find((candidate) => candidate.id === layer.sourceId)
          : undefined;
        if (!source || !isVectorSource(source) || !source.extent) return state;
        return {
          project: {
            ...state.project,
            map: { ...state.project.map, view: viewForExtent(source.extent, viewport) },
          },
          shell: { ...state.shell, inspectedFeature: null },
        };
      }),
    setMockEdition: (edition) =>
      set((state) => {
        const entitlement = createMockEntitlement(edition);
        if (
          state.mode !== 'composition' ||
          isCapabilityAvailable(entitlement, 'composition.workspace')
        ) {
          return { entitlement };
        }
        return {
          entitlement,
          mode: 'viewer',
          shell: {
            ...state.shell,
            attributeTableOpen: false,
            inspectedFeature: null,
            selectedCompositionElement: null,
            selectionToolActive: false,
            selectionMethod: null,
            selectedFeatures: [],
          },
        };
      }),
  };
}
